// src/components/authorizations/authorizationFormatters.tsx
//
// Shared presentation helpers for authorization UI.
// Extracted from AuthorizationDetails.tsx so TestConnectionSection and
// other drawer sections can reuse the same visuals.

import { Badge, Group, Text } from "@mantine/core";

/**
 * Render a Unix-seconds timestamp as a short Europe/Amsterdam string,
 * plus a relative suffix ("in 12 days" / "expired") when we're within
 * two weeks on either side — that's the window users typically care
 * about. Returns null for missing / zero input so the row gets omitted.
 */
export function formatExpiry(unix: unknown): string | null {
  if (typeof unix !== "number" || !unix) return null;
  const ms = unix * 1000;
  const date = new Date(ms);
  const abs = date.toLocaleString("nl-NL", {
    dateStyle: "medium",
    timeStyle: "short",
  });
  const diffDays = Math.round((ms - Date.now()) / (24 * 3600 * 1000));
  if (diffDays <= 0) return `${abs} (expired)`;
  if (diffDays <= 14) return `${abs} (in ${diffDays} day${diffDays === 1 ? "" : "s"})`;
  return abs;
}

/**
 * Render a list of OAuth/access-token scopes as a right-aligned wrap of
 * small gray badges, labeled "Scopes" on the left. Returns null when the
 * input is empty so callers don't need to guard.
 *
 * Optional `label` lets the caller override "Scopes" (useful for a
 * secondary "Stored scopes" list next to the live one).
 */
export function ScopeList({
  scopes,
  label = "Scopes",
}: {
  scopes: unknown;
  label?: string;
}) {
  if (!Array.isArray(scopes) || scopes.length === 0) return null;
  return (
    <Group gap="xs" justify="space-between" wrap="nowrap" align="flex-start">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Group
        gap="xs"
        wrap="wrap"
        justify="flex-end"
        aria-label={`${label}: ${scopes.join(", ")}`}
        style={{ maxWidth: "70%" }}
      >
        {scopes.map((scope) => (
          <Badge key={String(scope)} size="xs" variant="light" color="gray">
            {String(scope)}
          </Badge>
        ))}
      </Group>
    </Group>
  );
}
